/**
 * Motor de fluxo TARP ramificado — grafo de decisão (GUPM-170).
 * -------------------------------------------------------------
 * Fiel ao protótipo v5.10 (`buildInstFlow` etc.). Substitui o checklist
 * linear por um grafo: cada nó é uma decisão (sim/não), ação (next),
 * diagnóstico, ou um terminal (baixa/paebm/fim).
 *
 * A execução guarda o CAMINHO percorrido (path). A UI (GUPM-173) mostra TODOS
 * os nós em ordem canônica (`flow.order`), com estado por nó (atual/seguida/
 * pulada) — fiel ao `renderFlowSteps` do protótipo. Decisões são re-respondíveis
 * (refazem o caminho a jusante) e laços não duplicam o path (`decideAt`,
 * `toggleAction`, `nodeExecState`, `orderedNodeIds`).
 *
 * TS puro: sem React, sem Supabase, sem DOM. Opera sobre QUALQUER `Flow`
 * (os 9 fluxos concretos e o mapa gatilho→TARP são GUPM-171).
 */

export type FlowNodeType =
  | 'inicio'
  | 'decisao'
  | 'acao'
  | 'diagnostico'
  | 'baixa' // terminal → handoff ao RT
  | 'paebm' // terminal → emergência / PAEBM
  | 'fim'; // terminal → encerra sem escalonamento

/** Ação de WhatsApp anexada a um nó (to vazio = contato a preencher). */
export interface WhatsAppAction {
  to: string;
  msg: string;
  label: string;
}

export interface FlowNode {
  id: string;
  tipo: FlowNodeType;
  texto: string;
  // decisão:
  sim?: string;
  nao?: string;
  simNote?: string;
  naoNote?: string;
  // ação / início / diagnóstico:
  next?: string;
  wa?: WhatsAppAction;
  // terminais:
  handoff?: boolean;
  paebm?: boolean;
  /**
   * Terminal `fim` que representa "nada a registrar" (ex.: anomalia visual não
   * confirmada → rotina). Muda o desfecho de 'fim' para 'rotina' (GUPM-190 T1):
   * a UI não deve oferecer registrar/abrir a TARP — só descartar/cancelar.
   */
  semRegistro?: boolean;
}

export interface Flow {
  start: string;
  nodes: Record<string, FlowNode>;
  /** Ordem de exibição opcional (usada pela UI). */
  order?: string[];
  /** Rótulos curtos dos nós, opcional (ex.: '6a' → '6'). */
  labels?: Record<string, string>;
}

/** Estado da execução: só o caminho percorrido. */
export interface FlowState {
  path: string[];
}

export type FlowChoice = 'sim' | 'nao' | 'next';

const TERMINALS: ReadonlySet<FlowNodeType> = new Set(['baixa', 'paebm', 'fim']);
const ADVANCE_BY_NEXT: ReadonlySet<FlowNodeType> = new Set(['inicio', 'acao', 'diagnostico']);

export function startFlow(flow: Flow): FlowState {
  return { path: [flow.start] };
}

export function currentNodeId(state: FlowState): string {
  const id = state.path[state.path.length - 1];
  if (!id) throw new Error('FlowState com caminho vazio');
  return id;
}

export function currentNode(flow: Flow, state: FlowState): FlowNode {
  const id = currentNodeId(state);
  const node = flow.nodes[id];
  if (!node) throw new Error(`nó desconhecido no fluxo: ${id}`);
  return node;
}

export function isTerminal(node: FlowNode): boolean {
  return TERMINALS.has(node.tipo);
}

/**
 * Desfecho de um terminal do fluxo:
 * - `handoff` (baixa) → encaminha ao RT; `paebm` → emergência; `fim` → concluído
 *   com ocorrência (registrável); `rotina` → concluído SEM registro (nada anormal).
 * `null` para nós não-terminais.
 */
export type Outcome = 'handoff' | 'paebm' | 'fim' | 'rotina';

/** Desfecho do nó terminal (ou null se não-terminal). */
export function outcome(node: FlowNode): Outcome | null {
  switch (node.tipo) {
    case 'baixa':
      return 'handoff';
    case 'paebm':
      return 'paebm';
    case 'fim':
      return node.semRegistro ? 'rotina' : 'fim';
    default:
      return null;
  }
}

/** Próximo id dado o nó e a escolha; null se a escolha não se aplica. */
export function nextNodeId(node: FlowNode, choice: FlowChoice): string | null {
  if (node.tipo === 'decisao') {
    if (choice === 'sim') return node.sim ?? null;
    if (choice === 'nao') return node.nao ?? null;
    return null;
  }
  if (ADVANCE_BY_NEXT.has(node.tipo) && choice === 'next') {
    return node.next ?? null;
  }
  return null;
}

/**
 * Avança o fluxo. No-op (retorna o mesmo estado) se o nó é terminal ou a
 * escolha é inválida / aponta para nó inexistente.
 */
export function advance(flow: Flow, state: FlowState, choice: FlowChoice): FlowState {
  const node = currentNode(flow, state);
  if (isTerminal(node)) return state;
  const nextId = nextNodeId(node, choice);
  if (!nextId || !flow.nodes[nextId]) return state;
  return { path: [...state.path, nextId] };
}

/** Volta um nó (no-op no start). */
export function back(state: FlowState): FlowState {
  if (state.path.length <= 1) return state;
  return { path: state.path.slice(0, -1) };
}

/** Nós do caminho percorrido, na ordem. */
export function visitedNodes(flow: Flow, state: FlowState): FlowNode[] {
  return state.path
    .map((id) => flow.nodes[id])
    .filter((n): n is FlowNode => n !== undefined);
}

// ── Execução re-respondível para a UI (GUPM-173) ──────────────
// A UI mostra todos os nós; estes helpers navegam de forma loop-safe e
// permitem trocar a resposta de qualquer decisão já percorrida.

export type NodeExecState = 'atual' | 'seguida' | 'pulada';

/**
 * Estado inicial de uma execução: encadeia automaticamente os nós 'inicio'
 * (texto passivo, sem interação) — fiel a `flowInit` do protótipo.
 */
export function beginFlow(flow: Flow): FlowState {
  let state: FlowState = { path: [flow.start] };
  let guard = 0;
  while (guard++ < 50) {
    const node = currentNode(flow, state);
    if (node.tipo === 'inicio' && node.next && flow.nodes[node.next]) {
      state = { path: [...state.path, node.next] };
    } else break;
  }
  return state;
}

/** Ordem canônica de exibição dos nós: `flow.order` ou BFS a partir do start. */
export function orderedNodeIds(flow: Flow): string[] {
  if (flow.order && flow.order.length > 0) {
    return flow.order.filter((id) => flow.nodes[id]);
  }
  const seen = new Set<string>();
  const out: string[] = [];
  const queue: string[] = [flow.start];
  while (queue.length > 0) {
    const id = queue.shift();
    if (id === undefined || seen.has(id) || !flow.nodes[id]) continue;
    seen.add(id);
    out.push(id);
    const n = flow.nodes[id];
    for (const ref of [n.next, n.sim, n.nao]) if (ref) queue.push(ref);
  }
  return out;
}

/** Avança para `nextId` sem duplicar em laços (remove a ocorrência anterior). */
function goToLoopSafe(state: FlowState, nextId: string): FlowState {
  const prev = state.path.indexOf(nextId);
  const base = prev >= 0 ? state.path.slice(0, prev) : state.path;
  return { path: [...base, nextId] };
}

/**
 * Responde (ou re-responde) uma decisão: trunca o caminho a partir dela e
 * salta para o ramo escolhido. No-op se o nó não for decisão do caminho.
 */
export function decideAt(
  flow: Flow,
  state: FlowState,
  decisionId: string,
  choice: 'sim' | 'nao',
): FlowState {
  const node = flow.nodes[decisionId];
  if (!node || node.tipo !== 'decisao') return state;
  const idx = state.path.indexOf(decisionId);
  if (idx < 0) return state;
  const target = choice === 'sim' ? node.sim : node.nao;
  if (!target || !flow.nodes[target]) return state;
  return goToLoopSafe({ path: state.path.slice(0, idx + 1) }, target);
}

/**
 * Alterna a conclusão de uma ação: marcar avança (via `next`), desmarcar volta
 * ao ponto da ação (descartando o que veio depois). No-op fora do caminho.
 */
export function toggleAction(flow: Flow, state: FlowState, actionId: string): FlowState {
  const node = flow.nodes[actionId];
  if (!node) return state;
  const idx = state.path.indexOf(actionId);
  if (idx < 0) return state;
  if (idx < state.path.length - 1) {
    return { path: state.path.slice(0, idx + 1) }; // desmarca
  }
  if (!node.next || !flow.nodes[node.next]) return state;
  return goToLoopSafe(state, node.next); // conclui e avança
}

/** Resposta dada a uma decisão do caminho ('sim'/'nao'), ou null se não respondida. */
export function answerAt(flow: Flow, state: FlowState, decisionId: string): 'sim' | 'nao' | null {
  const idx = state.path.indexOf(decisionId);
  if (idx < 0 || idx >= state.path.length - 1) return null;
  const next = state.path[idx + 1];
  const node = flow.nodes[decisionId];
  if (!node) return null;
  if (next === node.sim) return 'sim';
  if (next === node.nao) return 'nao';
  return null;
}

/** Ação já concluída? (está no caminho e não é o nó atual). */
export function isActionDone(state: FlowState, actionId: string): boolean {
  const idx = state.path.indexOf(actionId);
  return idx >= 0 && idx < state.path.length - 1;
}

/**
 * Estado de exibição de um nó: 'atual' (fronteira ativa), 'seguida' (no
 * caminho), 'pulada' (fora do caminho — visível mas esmaecida/desabilitada).
 * Ao atingir um terminal, nenhum nó fica 'atual' (o terminal conta como seguida).
 */
export function nodeExecState(flow: Flow, state: FlowState, nodeId: string): NodeExecState {
  const curId = currentNodeId(state);
  const curTerminal = isTerminal(currentNode(flow, state));
  if (nodeId === curId && !curTerminal) return 'atual';
  if (state.path.includes(nodeId)) return 'seguida';
  return 'pulada';
}

/**
 * Valida a integridade estrutural do fluxo: referências (sim/nao/next)
 * existentes, decisão com ambos os ramos, nó de avanço com next.
 * Retorna a lista de erros ([] = íntegro). Usado nos testes do catálogo (171).
 */
export function validateFlow(flow: Flow): string[] {
  const errors: string[] = [];
  if (!flow.nodes[flow.start]) errors.push(`start '${flow.start}' inexistente`);
  for (const [id, node] of Object.entries(flow.nodes)) {
    for (const ref of [node.sim, node.nao, node.next]) {
      if (ref && !flow.nodes[ref]) errors.push(`nó '${id}' aponta para '${ref}' inexistente`);
    }
    if (node.tipo === 'decisao' && (!node.sim || !node.nao)) {
      errors.push(`decisão '${id}' precisa de 'sim' e 'nao'`);
    }
    if (ADVANCE_BY_NEXT.has(node.tipo) && !node.next) {
      errors.push(`nó '${id}' (${node.tipo}) sem 'next'`);
    }
  }
  return errors;
}
